import { Text, Divider, Switch } from '@/shared/ui/primitives';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import { TEST_IDS } from '@/testIds';
import { EditableSectionCard, FieldError } from '@/shared/ui';
import { useUnlimitedToggle } from '@/shared/hooks';
import {
  compareCalendarDates,
  getTodayForInput,
  parseCalendarDate,
  parseCalendarDateInput,
  type CalendarDate,
} from '@/shared/lib/calendarDate';
import { durationDaysBetween, validatePassEndDate, type PassFormValues } from '../../model';
import { InlineCalendar, type FocusedPill } from './InlineCalendar';
import styles from './PassModal.module.css';

const PILL_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
});

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat(undefined, { weekday: 'short' });

const SET_OPTIONS = {
  shouldDirty: true,
  shouldValidate: true,
  shouldTouch: true,
} as const;

const formatPillDate = (value: string): string => {
  const date = parseCalendarDate(value);
  return Number.isNaN(date.getTime()) ? '' : PILL_DATE_FORMATTER.format(date);
};

const formatPillWeekday = (value: string): string => {
  const date = parseCalendarDate(value);
  return Number.isNaN(date.getTime()) ? '' : WEEKDAY_FORMATTER.format(date);
};

export const DateRangeField: FC = () => {
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useFormContext<PassFormValues>();

  useController({
    name: 'startDate',
    control,
    rules: { required: 'Start date is required' },
  });

  useController({
    name: 'endDate',
    control,
    rules: {
      validate: (value) =>
        validatePassEndDate(value, getValues('startDate'), getValues('isUnlimitedDuration')),
    },
  });

  const { isUnlimited: isUnlimitedDuration, toggle } = useUnlimitedToggle<PassFormValues>({
    valueName: 'endDate',
    toggleName: 'isUnlimitedDuration',
    getFallback: (last, get) => last || get('endDate') || get('startDate') || getTodayForInput(),
  });

  const startDate = useWatch({ control, name: 'startDate' });
  const endDate = useWatch({ control, name: 'endDate' });

  const [focused, setFocused] = useState<FocusedPill | null>(null);
  const [calendarRole, setCalendarRole] = useState<FocusedPill>('start');
  const [calendarSession, setCalendarSession] = useState(0);
  const dateAreaRef = useRef<HTMLDivElement>(null);
  const startPillRef = useRef<HTMLButtonElement>(null);
  const endPillRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isUnlimitedDuration && focused === 'end') {
      setFocused(null);
    }
  }, [isUnlimitedDuration, focused]);

  useEffect(() => {
    if (focused === null) return;
    const handleDocClick = (event: MouseEvent): void => {
      const area = dateAreaRef.current;
      if (!area) return;
      if (!event.composedPath().includes(area)) {
        setFocused(null);
      }
    };
    document.addEventListener('click', handleDocClick);
    return () => document.removeEventListener('click', handleDocClick);
  }, [focused]);

  const togglePill = (role: FocusedPill): void => {
    if (focused === null) setCalendarSession((session) => session + 1);
    setCalendarRole(role);
    setFocused((prev) => (prev === role ? null : role));
  };

  const collapseEditor = useCallback((): void => {
    setFocused(null);
  }, []);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    void trigger('endDate');
  }, [startDate, isUnlimitedDuration, trigger]);

  const days = isUnlimitedDuration ? null : durationDaysBetween(startDate, endDate);
  const showInvalidRange =
    !isUnlimitedDuration &&
    Boolean(parseCalendarDateInput(startDate)) &&
    Boolean(parseCalendarDateInput(endDate)) &&
    compareCalendarDates(startDate, endDate) > 0;

  const setFocusedDate = useCallback(
    (next: CalendarDate): void => {
      if (calendarRole === 'start') {
        setValue('startDate', next, SET_OPTIONS);
        if (!isUnlimitedDuration) {
          setCalendarRole('end');
          setFocused('end');
          queueMicrotask(() => endPillRef.current?.focus());
        } else {
          setFocused(null);
          queueMicrotask(() => startPillRef.current?.focus());
        }
      } else {
        setValue('endDate', next, SET_OPTIONS);
        setFocused(null);
        queueMicrotask(() => endPillRef.current?.focus());
      }
    },
    [calendarRole, isUnlimitedDuration, setValue]
  );

  const renderInteractivePill = (
    role: FocusedPill,
    label: string,
    dateValue: string,
    testId: string,
    invalid: boolean
  ) => {
    const isFocused = focused === role;
    const weekday = formatPillWeekday(dateValue);
    const dateText = formatPillDate(dateValue);
    const pillClass = [styles.pill, isFocused && styles.pillFocused, invalid && styles.pillInvalid]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={role === 'start' ? startPillRef : endPillRef}
        type="button"
        className={pillClass}
        onClick={() => togglePill(role)}
        data-testid={testId}
        aria-pressed={isFocused}
      >
        <div className={styles.pillBody}>
          <Text variant="caption" weight="regular" className={styles.pillLabel}>
            {label}
            {weekday ? ` · ${weekday}` : ''}
          </Text>
          <span className={styles.pillValue}>{dateText || 'Pick'}</span>
        </div>
      </button>
    );
  };

  const startSummary = formatPillDate(startDate) || 'Pick start date';
  const endSummary = isUnlimitedDuration
    ? 'No end date'
    : formatPillDate(endDate) || 'Pick end date';

  return (
    <>
      <EditableSectionCard
        label="Duration"
        summary={`${startSummary} → ${endSummary}`}
        focusTargetRef={startPillRef}
        invalid={Boolean(errors.startDate || errors.endDate) || showInvalidRange}
        triggerTestId={TEST_IDS.durationEditorTrigger}
        onCollapse={collapseEditor}
        rightSlot={
          showInvalidRange ? (
            <Text variant="caption" weight="medium" className={styles.durationInvalid}>
              Invalid range
            </Text>
          ) : days !== null ? (
            <Text variant="caption" weight="medium" className={styles.durationHint}>
              {days} days
            </Text>
          ) : null
        }
      >
        <div className={styles.dateInteraction} ref={dateAreaRef}>
          <div className={styles.dateRange}>
            {renderInteractivePill(
              'start',
              'Start',
              startDate,
              TEST_IDS.passDateStartPill,
              Boolean(errors.startDate)
            )}
            <Text className={styles.dateArrow} aria-hidden="true">
              →
            </Text>
            {isUnlimitedDuration ? (
              <div
                className={`${styles.pill} ${styles.pillDisabled}`}
                data-testid={TEST_IDS.passDateEndPill}
                aria-disabled="true"
              >
                <div className={styles.pillBody}>
                  <Text variant="caption" weight="regular" className={styles.pillLabel}>
                    End
                  </Text>
                  <span className={styles.pillValue}>∞</span>
                </div>
              </div>
            ) : (
              renderInteractivePill(
                'end',
                'End',
                endDate,
                TEST_IDS.passDateEndPill,
                Boolean(errors.endDate)
              )
            )}
          </div>

          <div
            className={styles.calendarShell}
            data-testid={TEST_IDS.passCalendarShell}
            data-open={focused !== null}
            aria-hidden={focused === null}
            inert={focused === null}
          >
            <div className={styles.calendarClip}>
              <div className={styles.calendarContent}>
                <InlineCalendar
                  key={`${calendarRole}:${calendarSession}`}
                  today={getTodayForInput()}
                  startDate={startDate}
                  endDate={endDate}
                  isUnlimitedDuration={isUnlimitedDuration}
                  focused={calendarRole}
                  onDayClick={setFocusedDate}
                />
              </div>
            </div>
          </div>
        </div>

        <Divider />

        <div className={styles.toggleRow}>
          <Text weight="regular">No end date</Text>
          <Switch
            checked={isUnlimitedDuration}
            onChange={(e) => toggle(e.target.checked)}
            aria-label="Toggle unlimited duration"
            data-testid={TEST_IDS.passUnlimitedDurationToggle}
          />
        </div>
      </EditableSectionCard>
      <FieldError message={errors.startDate?.message?.toString()} />
      <FieldError message={errors.endDate?.message?.toString()} />
    </>
  );
};
